import { z } from 'zod';

import { apiListQuery, createQueryKeys } from '@/shared/data';
import { defineEntitySource } from '@/shared/form/entity-source';
import { billingApi, getCurrentTenant } from '@/shared/http/client';

// Porta de busca de carteira (F0, discovery/10 §4): o que outros módulos importam de `wallet/public`.
// Arquivo à parte e mínimo, para que a rota de quem busca carteira não arraste o fio inteiro das telas
// de Carteiras (31 chamadas, esquemas, downloads). Assinaturas inalteradas desde a F0.

const text = z.string().nullish();

/** Opção de carteira do autocomplete "Código do convênio": o legado lê `id`, `walletCode` e `beneficiaryName`. */
export const walletOptionSchema = z.looseObject({ id: text, walletCode: z.number(), beneficiaryName: text });
export const walletOptionPageSchema = z.looseObject({ data: z.array(walletOptionSchema), page: z.number(), totalItems: z.number(), totalPages: z.number(), paginationToken: text });
export type WalletOption = z.infer<typeof walletOptionSchema>;

export const walletKeys = createQueryKeys('wallet');

function walletSearch(search: string | undefined): Record<string, string> {
  if (search === undefined || search.trim() === '') return {};
  return /^\d+$/.test(search) ? { walletsCode: search } : { name: search };
}

/**
 * `GET BillingApi /Wallet` (autocomplete "Código do convênio") — UYZY@e7f4d7b25 `services/walletManagement/wallet/wallets.ts:24`;
 * query de `contexts/wallet/walletContext/walletContext.tsx:63-68` (`{ busca, page: 0, size: 10, registrationNumber, tenant }`).
 * Porta de busca F0-min (discovery/10 §4): réguas de cobrança e comunicação consomem daqui.
 */
export async function listWalletOptions(
  options: { registrationNumber?: string | undefined; search?: string | undefined; tenant: string | undefined },
  signal?: AbortSignal,
) {
  const params = { ...walletSearch(options.search), page: 0, size: 10, registrationNumber: options.registrationNumber, tenant: options.tenant };
  const data = await billingApi.get<unknown>('/Wallet', { params, ...(signal ? { signal } : {}) });
  // U27: fio fora do contrato só avisa fora de produção e segue com o valor cru.
  const parsed = walletOptionPageSchema.safeParse(data);
  if (parsed.success) return parsed.data;
  if (!import.meta.env.PROD) console.warn('GET /Wallet fora do contrato', parsed.error.issues);
  return data as z.output<typeof walletOptionPageSchema>;
}

/**
 * Opções de carteira ("Código do convênio"). Só com `BillingAccount:Read`
 * (`walletContext.tsx:71`): quem chama passa `enabled`.
 */
export function walletOptionsQuery(
  params: { registrationNumber?: string | undefined; search?: string | undefined; tenant?: string | undefined },
  extra: { enabled?: boolean } = {},
) {
  const resolved = { ...params, tenant: 'tenant' in params ? params.tenant : getCurrentTenant() };
  return apiListQuery(walletKeys.custom('options', resolved), ({ signal }) => listWalletOptions(resolved, signal), extra);
}

/** Seletor de carteira dos outros módulos: rótulo "(código) - beneficiário", valor o código. */
export function walletSource(registrationNumber?: string) {
  return defineEntitySource<WalletOption>({
    key: ['wallet', 'source', registrationNumber ?? ''],
    scope: getCurrentTenant,
    search: async (query, signal) =>
      (
        await listWalletOptions(
          { registrationNumber, search: query || undefined, tenant: getCurrentTenant() },
          signal,
        )
      ).data,
    toOption: (wallet) => ({
      value: String(wallet.walletCode),
      label: `(${String(wallet.walletCode)}) - ${wallet.beneficiaryName ?? ''}`,
    }),
  });
}
